// rs-car-clearance.ts

export type Fuel = 'petrol' | 'hybrid' | 'diesel' | 'electric';

export interface Input {
  manufactureYear: number;        // e.g., 2025
  engineLiters?: number;          // e.g., 2.0 (ignored for pure Electric per RS UI)
  engineCc?: number;              // alternative to engineLiters
  fuel: Fuel;
  usedDays?: number;              // each day adds 5 GEL to TOTAL (per RS UI)
  rightWheelMoved?: boolean;      // RS checkbox
  sport?: boolean;                // RS checkbox
  classic?: boolean;              // RS checkbox
  importYear?: number;            // defaults to current year
}

export interface Config {
  // Petrol age-factor schedule (GEL/cc) from your sheet/screens.
  petrolAgeFactors: { maxYearsOld: number; factor: number; label: string }[];
  petrolOver14: { factor: number; label: string };

  /**
   * Multipliers applied to the petrol age factor by fuel.
   * Verified: hybrid = 0.4; electric = 0 (excise 0).
   * Diesel left = petrol unless you provide specific values.
   */
  fuelMultipliers: Partial<Record<Fuel, number>>;

  /**
   * Option adjustments. If RS applies adders instead of pure multipliers,
   * use fixedAdderPerCc to model that precisely.
   */
  optionAdjustments?: {
    rightWheelMoved?: { multiplier?: number; fixedAdderPerCc?: number };
    sport?:           { multiplier?: number; fixedAdderPerCc?: number };
    classic?:         { multiplier?: number; fixedAdderPerCc?: number };
  };

  /**
   * Customs-tax base (GEL) for a 2.0-L engine by manufacture year,
   * scaled by (engineLiters / 2.0). (Electric → always 0.)
   */
  customsBase2L: Record<number, number>;
  defaultCustomsBase2L: number;

  /** RS front-end “MIA service” fixed fees (from their JS). */
  miaServiceFeesGel: number;      // 150+50+30+50+100+80+20 = 480

  /** RS “used days” fee per day. */
  usedDayFeeGel: number;          // 5
}

export interface Breakdown {
  exciseGEL: number;
  customsTaxGEL: number;
  miaServiceFeesGEL: number;
  usedDaysFeeGEL: number;
  subtotalGEL: number;            // excise + customs
  totalGEL: number;               // subtotal + fees
  detail: {
    engineCc: number;
    engineLiters: number;
    yearsOld: number;
    ageBandLabel: string;
    petrolAgeFactor: number;
    fuelMultiplier: number;
    optionMultiplier: number;
    fixedAdderPerCc: number;
    appliedExcisePerCc: number;
    customsBaseFor2L: number;
    electricRightWheelOverride: boolean;
  };
}

/* ========================= Defaults (from evidence) ========================= */

const PETROL_AGE_FACTORS_DEFAULT: Config['petrolAgeFactors'] = [
  { maxYearsOld: 0,  factor: 1.50, label: 'under 1' },
  { maxYearsOld: 1,  factor: 1.50, label: '1' },
  { maxYearsOld: 2,  factor: 1.50, label: '2' },
  { maxYearsOld: 3,  factor: 1.40, label: '3' },
  { maxYearsOld: 4,  factor: 1.20, label: '4' },
  { maxYearsOld: 5,  factor: 1.00, label: '5' },
  { maxYearsOld: 6,  factor: 0.80, label: '6' },
  { maxYearsOld: 7,  factor: 0.80, label: '7' },
  { maxYearsOld: 8,  factor: 0.80, label: '8' },
  { maxYearsOld: 9,  factor: 0.90, label: '9' },
  { maxYearsOld: 10, factor: 1.10, label: '10' },
  { maxYearsOld: 11, factor: 1.30, label: '11' },
  { maxYearsOld: 12, factor: 1.50, label: '12' },
  { maxYearsOld: 13, factor: 1.80, label: '13' },
  { maxYearsOld: 14, factor: 2.10, label: '14' },
];
const PETROL_OVER14_DEFAULT: Config['petrolOver14'] = { factor: 2.40, label: 'over 14' };

// From your screenshots for 2.0 L:
const CUSTOMS_BASE_2L_DEFAULT: Config['customsBase2L'] = {
  2025: 105,
  2024: 105,
  2023: 110,
  2022: 115,
  2021: 120,
  2020: 125,
  2019: 130,
  2018: 135,
  2017: 140,
  2016: 145,
  2015: 150,
  2014: 155,
  2013: 160,
  2012: 165,
  2011: 170,
  2010: 175,
  2009: 180,
  2008: 185,
  2007: 190,
  2006: 195,
  2005: 200,
  2004: 205,
  2003: 210,
  2002: 215,
  2001: 220,
  2000: 225,
  1999: 230,
  1998: 235,
  1997: 240,
  1996: 245,
  1995: 250,
};

export const DEFAULT_CONFIG: Config = {
  petrolAgeFactors: PETROL_AGE_FACTORS_DEFAULT,
  petrolOver14: PETROL_OVER14_DEFAULT,
  fuelMultipliers: {
    petrol: 1,
    hybrid: 0.4,    // verified by RS
    diesel: 1,      // keep neutral (same as petrol) unless you provide RS factors
    electric: 0,    // RS: excise 0 (year/engine disabled)
  },
  optionAdjustments: {
    // Neutral until you provide RS evidence for surcharges/discounts:
    rightWheelMoved: { multiplier: 1 },
    sport:           { multiplier: 1 },
    classic:         { multiplier: 1 },
  },
  customsBase2L: CUSTOMS_BASE_2L_DEFAULT,
  defaultCustomsBase2L: 105,
  miaServiceFeesGel: 480,
  usedDayFeeGel: 5,
};

const CAR_ID_TO_DEBUG = 1845438;
/* =============================== Implementation ============================ */

const r2 = (n: number) => Math.round(n * 100) / 100;

function resolveEngine(engineCc?: number, engineLiters?: number) {
  // For pure Electric RS disables engine; default to the UI’s minimum 0.1L-equivalent (100 cc)
  if (!engineCc && !engineLiters) {
    return { cc: 1500, liters: 1.0 };
  }
  if (engineCc && engineCc > 0) return { cc: Math.round(engineCc), liters: engineCc / 1000 };
  if (engineLiters && engineLiters > 0)
    return { cc: Math.round(engineLiters * 1000), liters: engineLiters };
  throw new Error('Provide engine size via engineCc or engineLiters (>0).');
}

function pickAgeBand(manufactureYear: number, importYear: number, cfg: Config) {
  const yearsOld = Math.max(0, importYear - manufactureYear);
  const row = cfg.petrolAgeFactors.find((r) => yearsOld <= r.maxYearsOld);
  return row
    ? { yearsOld, factor: row.factor, label: row.label }
    : { yearsOld, factor: cfg.petrolOver14.factor, label: cfg.petrolOver14.label };
}

/**
 * Full RS-like calculation:
 *  - Excise:
 *      * Electric: 0, unless Right Wheel/Moved → use Petrol rules (per your screenshots).
 *      * Otherwise: engine_cc × petrolAgeFactor × fuelMultiplier × optionMultiplier (+ adders).
 *  - Customs line:
 *      * Electric: 0 (per RS).
 *      * Otherwise: customsBase2L[year] × (engineLiters / 2.0).
 *  - MIA fees: always 480 GEL (from site JS).
 *  - Used days: +5 GEL/day to the grand total.
 */
export function calcCarClearance(
  input: Input,
  car: any = null,
  cfg: Config = DEFAULT_CONFIG,
): Breakdown {
  // if (car?.id == CAR_ID_TO_DEBUG) {
  //   debugger;
  // }
  const importYear = input.importYear ?? new Date().getFullYear();
  const { yearsOld, factor: petrolAgeFactor, label: ageBandLabel } =
    pickAgeBand(input.manufactureYear, importYear, cfg);

  // Engine (Electric ignores, but we keep a harmless default for completeness)
  const { cc, liters } = resolveEngine(input.engineCc, input.engineLiters);

  // Electric special-case from your screenshots:
  // - Electric alone: excise=0, customs=0 (total = 480 + usedDays*5)
  // - Electric + RightWheel/Moved: behaves like Petrol for excise; customs remains 0
  const electricRightWheelOverride =
    input.fuel === 'electric' && !!input.rightWheelMoved;

  // Fuel multiplier (Electric overridden when RHD)
  const baseFuel: Fuel = electricRightWheelOverride ? 'petrol' : input.fuel;
  const fuelMult = cfg.fuelMultipliers[baseFuel];
  if (fuelMult === undefined) {
    throw new Error(
      `Missing multiplier for fuel='${baseFuel}'. Supply it in Config.fuelMultipliers.`
    );
  }
  let appliedFuelMultiplier = fuelMult;

  // Option multipliers/adders (neutral unless you set them)
  const adj = cfg.optionAdjustments ?? {};
  const rwMult = input.rightWheelMoved ? (adj.rightWheelMoved?.multiplier ?? 1) : 1;
  const sportMult = input.sport ? (adj.sport?.multiplier ?? 1) : 1;
  const classicMult = input.classic ? (adj.classic?.multiplier ?? 1) : 1;
  const optionMultiplier = rwMult * sportMult * classicMult;

  const fixedAdderPerCc =
    (input.rightWheelMoved ? (adj.rightWheelMoved?.fixedAdderPerCc ?? 0) : 0) +
    (input.sport ? (adj.sport?.fixedAdderPerCc ?? 0) : 0) +
    (input.classic ? (adj.classic?.fixedAdderPerCc ?? 0) : 0);

  // Excise
  let exciseGEL: number;
  if (input.fuel === 'electric' && !electricRightWheelOverride) {
    exciseGEL = 0; // RS behavior
  } else if (input.fuel === 'hybrid') {
    // Apply the 60% hybrid discount only for cars up to 6 years old;
    // for older hybrids, use the petrol multiplier (no discount).
    appliedFuelMultiplier = yearsOld > 6 ? 1 : fuelMult;
    const excisePerCc = petrolAgeFactor * appliedFuelMultiplier * optionMultiplier + fixedAdderPerCc;
    exciseGEL = r2(cc * excisePerCc);
    if (car?.id == CAR_ID_TO_DEBUG) {
      console.log('hybrid exciseGEL: cc * excisePerCc', exciseGEL);
    }
  } else {
    const excisePerCc = petrolAgeFactor * appliedFuelMultiplier * optionMultiplier + fixedAdderPerCc;
    exciseGEL = r2(cc * excisePerCc);
    if (car?.id == CAR_ID_TO_DEBUG) {
      console.log('excisePerCc: petrolAgeFactor * fuelMult * optionMultiplier + fixedAdderPerCc', excisePerCc);
      console.log('exciseGEL: cc * excisePerCc', exciseGEL);
    }
  }



  // Customs line
  let customsTaxGEL: number;
  if (input.fuel === 'electric') {
    customsTaxGEL = 0; // RS behavior
  } else {
    const base2L = cfg.customsBase2L[input.manufactureYear] ?? cfg.defaultCustomsBase2L;
    customsTaxGEL = r2(base2L * (liters / 2.0));
  }

  if (car?.id == CAR_ID_TO_DEBUG) {
    console.log('customsTaxGEL: base2L * (liters / 2.0)', customsTaxGEL);
  }

  // Fees
  const miaServiceFeesGEL = cfg.miaServiceFeesGel;
  const usedDaysFeeGEL = r2(Math.max(0, input.usedDays ?? 0) * cfg.usedDayFeeGel);

  if (car?.id == CAR_ID_TO_DEBUG) {
    console.log('miaServiceFeesGEL', miaServiceFeesGEL);
    console.log('usedDaysFeeGEL', usedDaysFeeGEL);
  }

  const subtotalGEL = r2(exciseGEL + customsTaxGEL);
  const totalGEL = r2(subtotalGEL + miaServiceFeesGEL + usedDaysFeeGEL);

  if (car?.id == CAR_ID_TO_DEBUG) {
    console.log('subtotalGEL', subtotalGEL);
    console.log('totalGEL = subtotalGEL + miaServiceFeesGEL + usedDaysFeeGEL', totalGEL);
  }

  return {
    exciseGEL,
    customsTaxGEL,
    miaServiceFeesGEL,
    usedDaysFeeGEL,
    subtotalGEL,
    totalGEL,
    detail: {
      engineCc: cc,
      engineLiters: liters,
      yearsOld,
      ageBandLabel,
      petrolAgeFactor,
      fuelMultiplier: appliedFuelMultiplier,
      optionMultiplier,
      fixedAdderPerCc,
      appliedExcisePerCc:
        input.fuel === 'electric' && !electricRightWheelOverride
          ? 0
          : petrolAgeFactor * appliedFuelMultiplier * optionMultiplier + fixedAdderPerCc,
      customsBaseFor2L: cfg.customsBase2L[input.manufactureYear] ?? cfg.defaultCustomsBase2L,
      electricRightWheelOverride,
    },
  };
}


/* =============================== Quick checks ===============================

1) Petrol, 2025, 2.0 L (your screenshot case):
   calcCarClearance({ manufactureYear: 2025, engineLiters: 2.0, fuel: 'petrol' })
   -> excise 3000, customs 105, total 3585

2) Hybrid, 2025, 0.1 L (your screenshot):
   -> excise 60, customs 5.25, total 545.25

3) Hybrid, 2025, 2.0 L (your screenshot):
   -> excise 1200, customs 105, total 1785

4) Electric (no RHD), 2025:
   -> excise 0, customs 0, total 480

5) Electric + Right Wheel/Moved, 2.0 L, 2025/2022/2021 (your screenshots):
   -> excise 3000 / 2800 / 2400; customs 0; total 3480 / 3280 / 2880

============================================================================= */


/**
 * Prefer FirstUseDate if present; otherwise return ManufactureDate.
 * Returns an ISO date string (e.g., "2023-01-01T00:00:00.000Z") or null.
 */
export function getCarRelevantDate(car: { FirstUseDate?: string | null; ManufactureDate?: string | null }): string | null {
  if (car?.FirstUseDate) return car.FirstUseDate;
  if (car?.ManufactureDate) return car.ManufactureDate;
  return null;
}

/**
 * Resolve manufacture year from FirstUseDate (preferred) or ManufactureDate.
 * Returns the UTC full year or null if neither date is available/parseable.
 */
export function getManufactureYearFromDates(car: { FirstUseDate?: string | null; ManufactureDate?: string | null }): number | null {
  const iso = getCarRelevantDate(car);
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.getUTCFullYear();
}
